import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EStorageKeys } from '../../shared/enums/storage-keys.enum';
import { ApiResponse, ILoginResponse } from '../../shared/models/api.model';
import { AuthService } from '../../shared/services/auth.service';
import { StorageService } from '../../shared/services/storage.service';

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const storageService = inject(StorageService);
  const router = inject(Router);
  const http = inject(HttpClient);
  // AuthService is resolved lazily (only inside logout(), which runs on a response error —
  // long after AuthService construction) so importing it here can't reintroduce the
  // bootstrap-time circular dependency (AuthService → HttpClient → interceptor).
  const injector = inject(Injector);

  // Token direkt synchron aus dem Storage holen
  const accessToken = storageService.getItem(EStorageKeys.ACCESS_TOKEN);

  // Token hinzufügen, Request weiterleiten und Fehler abfangen
  return next(addToken(accessToken, req)).pipe(
    catchError(err => {
      // 401 Unauthorized = the session itself is invalid (missing/expired/bad token).
      // Try to refresh if we still have a token, otherwise log out.
      if (err instanceof HttpErrorResponse && err.status === 401) {
        // if there are tokens then send refresh token request
        if (accessToken) return refreshToken(req, next);
        // otherwise logout and redirect to login page
        else return logout(err);
      }
      // 403 Forbidden means the user is authenticated but not permitted to do THIS specific
      // action (backend requireAdmin/requireRoles). It is NOT a session-invalid signal, so we
      // must NOT log out — that would kick a valid user out for using a feature they lack rights
      // to (e.g. BÜRO hitting the admin-only /api/order search), and previously caused a
      // logout→redirect→retry loop that flooded the API. Let the component handle the 403.
      return throwError(() => err);
    })
  );

  function logout(err: Error): Observable<HttpEvent<any>> {
    // Reset the FULL auth session (storage + AuthService subjects), not just storage.
    // If currentUser$ is left truthy, LoginComponent immediately redirects back to the
    // landing route, which re-fires the failing request → logout() → redirect → an
    // infinite loop that floods the API and crashes the tab.
    injector.get(AuthService).clearSession();
    router.navigate(['/login']);
    return throwError(() => err);
  }

  // Add our current access token from the service if present
  function addToken(accessToken: string, req: HttpRequest<any>) {
    if (accessToken) {
      return req.clone({
        setHeaders: {
          auth: accessToken,
        },
      });
    } else {
      return req;
    }
  }

  function refreshToken(request: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    // Implement token refresh without AuthService dependency
    const refreshToken = storageService.getItem(EStorageKeys.REFRESH_TOKEN);
    return http.post<ApiResponse<ILoginResponse>>(
      `${environment.apiUrl}/auth/refreshToken`,
      {
        refreshToken: refreshToken,
      }
    ).pipe(
      switchMap((res: ApiResponse<ILoginResponse>) => {
        const tokens = res.DATA as ILoginResponse;
        // Store the new tokens
        storageService.setItem(EStorageKeys.ACCESS_TOKEN, tokens.accessToken);
        storageService.setItem(EStorageKeys.REFRESH_TOKEN, tokens.refreshToken);
        return next(addToken(tokens.accessToken, request));
      })
    );
  }
};
